import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Box, Typography, Button, LinearProgress, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchNeuroQuestions,
  saveNeuroAnswers,
  submitNeuroEvaluation,
  setLocalAnswer,
  hydrateLocalAnswers,
} from '../../store/slices/neuroSlice';
import { flowLogger } from '../../utils/flowLogger';

const LIKERT_OPTIONS = [
  { value: 0, label: 'Pas du tout' },
  { value: 1, label: 'Parfois' },
  { value: 2, label: 'Souvent' },
];

const storageKey = (evalId) => `neuro_answers_${evalId}`;

const NeuroQuestionStep = ({ evaluation, kid, onComplete, onBack }) => {
  const dispatch = useDispatch();
  const { questions, localAnswers, loading } = useSelector((state) => state.neuro);
  const [position, setPosition] = useState(evaluation?.current_question_position || 1);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(null);
  const pendingRef = useRef([]);

  useEffect(() => {
    dispatch(fetchNeuroQuestions());
  }, [dispatch]);

  useEffect(() => {
    if (!evaluation?.id) return;
    const stored = sessionStorage.getItem(storageKey(evaluation.id));
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        dispatch(hydrateLocalAnswers(parsed));
      } catch { /* ignore */ }
    }
  }, [evaluation?.id, dispatch]);

  const currentQuestion = questions.find((q) => q.position === position);
  const total = questions.length || 15;
  const answeredCount = Object.keys(localAnswers).length;

  const persistLocal = useCallback((answers) => {
    if (!evaluation?.id) return;
    sessionStorage.setItem(storageKey(evaluation.id), JSON.stringify(answers));
  }, [evaluation?.id]);

  const flushSave = useCallback(async (answers, pos) => {
    if (!evaluation?.id) return;
    const payload = Object.entries(answers).map(([question_id, likert_value]) => ({
      question_id: parseInt(question_id, 10),
      likert_value,
    }));
    if (!payload.length) return;
    setSaving(true);
    try {
      await dispatch(saveNeuroAnswers({
        evaluationId: evaluation.id,
        answers: payload,
        current_position: pos,
      })).unwrap();
      sessionStorage.removeItem(storageKey(evaluation.id));
      flowLogger('neuro.answers.saved', { evaluationId: evaluation.id, count: payload.length });
    } catch (e) {
      persistLocal(answers);
      if (!navigator.onLine) {
        flowLogger('neuro.answers.offline', { evaluationId: evaluation.id });
      }
    } finally {
      setSaving(false);
    }
  }, [dispatch, evaluation?.id, persistLocal]);

  const scheduleSave = useCallback((answers, pos) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => flushSave(answers, pos), 500);
  }, [flushSave]);

  const handleSelect = (value) => {
    if (!currentQuestion) return;
    const next = { ...localAnswers, [currentQuestion.id]: value };
    dispatch(setLocalAnswer({ questionId: currentQuestion.id, value }));
    persistLocal(next);
    pendingRef.current = next;

    const shouldFlush = Object.keys(next).length % 2 === 0;
    if (shouldFlush) {
      flushSave(next, position);
    } else {
      scheduleSave(next, position);
    }
  };

  const handleNext = async () => {
    if (!currentQuestion || localAnswers[currentQuestion.id] == null) return;
    if (position < total) {
      setPosition(position + 1);
      return;
    }
    setSubmitting(true);
    try {
      await flushSave(localAnswers, total);
      const result = await dispatch(submitNeuroEvaluation(evaluation.id)).unwrap();
      flowLogger('neuro.evaluation.submitted', { evaluationId: evaluation.id });
      onComplete(result);
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (position > 1) setPosition(position - 1);
    else onBack();
  };

  if (loading && !questions.length) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#FFF9F0' }}>
        <CircularProgress sx={{ color: '#784e3c' }} />
      </Box>
    );
  }

  if (!currentQuestion) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', backgroundColor: '#FFF9F0', minHeight: '100vh' }}>
        <Typography>Chargement des questions...</Typography>
      </Box>
    );
  }

  const selected = localAnswers[currentQuestion.id];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#FFF9F0', px: 2, py: 3, maxWidth: 520, mx: 'auto' }}>
      <Typography variant="caption" sx={{ color: '#888' }}>
        Question {position} / {total}
        {saving && ' — sauvegarde...'}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={(answeredCount / total) * 100}
        sx={{ mb: 2, mt: 1, height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { backgroundColor: '#784e3c' } }}
      />
      <Typography variant="overline" sx={{ color: '#784e3c' }}>
        {currentQuestion.spectrum_label}
      </Typography>
      <Typography variant="h6" sx={{ color: '#333', mb: 4, lineHeight: 1.5, fontWeight: 500 }}>
        {currentQuestion.content}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 4 }}>
        {LIKERT_OPTIONS.map((opt) => (
          <Button
            key={opt.value}
            type="button"
            variant={selected === opt.value ? 'contained' : 'outlined'}
            onClick={() => handleSelect(opt.value)}
            sx={{
              minHeight: 48,
              touchAction: 'manipulation',
              justifyContent: 'flex-start',
              px: 3,
              backgroundColor: selected === opt.value ? '#784e3c' : 'transparent',
              borderColor: '#784e3c',
              color: selected === opt.value ? '#fff' : '#784e3c',
              '&:hover': { backgroundColor: selected === opt.value ? '#5f3e2f' : 'rgba(120,78,60,0.08)' },
            }}
          >
            {opt.label}
          </Button>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button type="button" onClick={handleBack} sx={{ minHeight: 48, flex: 1, color: '#784e3c' }}>
          Retour
        </Button>
        <Button
          type="button"
          variant="contained"
          disabled={selected == null || submitting}
          onClick={handleNext}
          sx={{ minHeight: 48, flex: 1, backgroundColor: '#784e3c' }}
        >
          {position < total ? 'Suivant' : (submitting ? 'Calcul...' : 'Voir mes résultats')}
        </Button>
      </Box>
    </Box>
  );
};

export default NeuroQuestionStep;
